// pulled from https://github.com/marmelab/react-admin/blob/master/examples/demo/src/layout/SubMenu.tsx

import * as React from 'react'
import { FC, ReactElement } from 'react'
import { MenuItemLink, useTranslate } from 'react-admin'
import { styled } from '@mui/material/styles'
import Collapse from '@mui/material/Collapse'
import DefaultIcon from '@mui/icons-material/ViewList'
import Divider from '@mui/material/Divider'
import ExpandMore from '@mui/icons-material/ExpandMore'
import List from '@mui/material/List'
import ListItemIcon from '@mui/material/ListItemIcon'
import MaterialUIMenuItem from '@mui/material/MenuItem'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'

import AppState from 'shared/app_state'

const PREFIX = 'SubMenu'

const classes = {
  icon: `${PREFIX}-icon`,
  iconInner: `${PREFIX}-iconInner`,
  iconInverse: `${PREFIX}-iconInverse`,
  sidebarIsOpen: `${PREFIX}-sidebarIsOpen`,
  sidebarIsClosed: `${PREFIX}-sidebarIsClosed`,
}

const Container = styled('div')(({ theme }) => ({
  [`& .${classes.icon}`]: { minWidth: theme.spacing(5) },
  [`& .${classes.iconInner}`]: { transition: 'transform .2s ease' },
  [`& .${classes.iconInverse}`]: { transform: 'rotate(-90deg)' },

  [`& .${classes.sidebarIsOpen}`]: {
    paddingLeft: 25,
  },

  [`& .${classes.sidebarIsClosed}`]: {
    paddingLeft: 0,
  },
}))

export interface SubMenuProps {
  dense: boolean
  onMenuClick: () => void
  sidebarIsOpen: boolean
}

export interface SubMenuWithResourcesProps extends SubMenuProps {
  resources: any[]
}

export type MenuItem = {
  name: string
  icon: any
  options?: { label: string | React.ReactNode; menu: string }[]
  route?: string
  onClick?: (...args: any[]) => void
}

interface Props {
  dense: boolean
  flatten?: boolean
  handleToggle: () => void
  icon?: ReactElement
  isOpen: boolean
  menuItems: MenuItem[]
  name: string
  onMenuClick: () => void
  sidebarIsOpen: boolean
  subMenuKey: string
  compareFunction?: (a: { name: string }, b: { name: string }) => number
}

const SubMenu: FC<Props> = ({
  dense,
  flatten = false,
  handleToggle,
  icon,
  isOpen,
  menuItems,
  name,
  onMenuClick,
  sidebarIsOpen,
  subMenuKey,
  compareFunction,
}) => {
  const translate = useTranslate()

  if (compareFunction) menuItems = menuItems.sort(compareFunction)

  const primaryTextFromOptions = (
    options: { label: string | React.ReactNode; menu: string }[]
  ): string | React.ReactNode => {
    if (options) {
      const option = options.find((o) => o.menu == subMenuKey)
      if (option && option.label) return option.label
    }
    return null
  }

  const header = flatten ? null : (
    <MaterialUIMenuItem dense={dense} button onClick={handleToggle} className="submenu-header">
      {!!icon && !isOpen && !sidebarIsOpen && (
        <ListItemIcon className={classes.icon}>
          {
            // (!!icon && !isOpen && !sidebarIsOpen)
            // ?
            icon
            // : (
            //   <ExpandMore className={[
            //     classes.iconInner,
            //     isOpen ? void 0 : classes.iconInverse
            //   ].join(' ')} />
            // )
          }
        </ListItemIcon>
      )}
      <Typography variant="inherit" color="textSecondary">
        {translate(name, { _: name })}
      </Typography>
    </MaterialUIMenuItem>
  )

  return (
    <Container>
      {sidebarIsOpen || isOpen ? (
        header
      ) : (
        <Tooltip title={translate(name)} placement="right">
          {header}
        </Tooltip>
      )}
      <Collapse in={isOpen} timeout="auto" unmountOnExit>
        <List
          dense={dense}
          component="div"
          disablePadding
          style={{
            transition: 'padding 195ms cubic-bezier(0.4, 0, 0.6, 1) 0ms',
          }}
          className={sidebarIsOpen && !flatten ? classes.sidebarIsOpen : classes.sidebarIsClosed}
        >
          {menuItems.map(({ name, route, icon: Icon, options, onClick }: MenuItem) => {
            let to = route || `/${name}`
            if (to.includes(':account_id')) to = to.replaceAll(':account_id', AppState.getCurrentAccount().id)
            return (
              <MenuItemLink
                key={`${subMenuKey}-${name}-menu-link`}
                to={to}
                primaryText={primaryTextFromOptions(options) || name}
                leftIcon={Icon ? <Icon /> : <DefaultIcon />}
                onClick={onClick || onMenuClick}
                sidebarIsOpen={sidebarIsOpen}
                dense={dense}
              />
            )
          })}
        </List>
      </Collapse>
    </Container>
  )
}

export default SubMenu
